import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Wrench,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Plus,
  RefreshCw,
  Search,
  Eye,
  Download,
  DollarSign,
  X,
  Calendar,
  FileText,
  Truck,
  Users,
  Building2,
  Bell,
  ChevronDown,
  Upload,
  Paperclip,
  Settings,
  Check,
} from "lucide-react";
import Swal from "sweetalert2";
import { DocumentDetailDrawer } from "./DocumentDetailDrawer.jsx";
import { RenewalWizard } from "./RenewalWizard.jsx";
import { KPICard } from "./KPICard.jsx";
import { AlertsDropdown } from "./AlertsDropdown.jsx";
import { FleetFilters } from "./FleetFilters.jsx";
import { CustomFields } from "./CustomFields";
import { RenewalWarningModal } from "./RenewalWarningModal";
const FleetManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unreadAlertsCount, setUnreadAlertsCount] = useState(0);
  const [canReload, setCanReload] = useState(true);
  const [customFieldsValues, setCustomFieldsValues] = useState({});
  // Filtros
  // const [searchTerm, setSearchTerm] = useState("");
  // const [statusFilter, setStatusFilter] = useState("all");
  // const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  // const [documentTypeFilter, setDocumentTypeFilter] = useState("all");
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    entityType: "all",
    documentType: "all",
  });
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);

  // Estados
  const [stats, setStats] = useState({
    activeDocuments: 0,
    warningDocuments: 0, // por vencer
    urgentDocuments: 0, // vencimiento próximo
    expiredDocuments: 0, // vencidos
    pendingMaintenances: 0,
  });

  // Modal
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showRenewalWizard, setShowRenewalWizard] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showRenewalWarning, setShowRenewalWarning] = useState(false);
  const [documentToRenew, setDocumentToRenew] = useState(null);
  const [showAlertsDropdown, setShowAlertsDropdown] = useState(false);
  const [formData, setFormData] = useState({
    document_type_id: "",
    documentable_type: "vehicle",
    documentable_id: "",
    // document_number: "",
    issue_date: "",
    expiration_date: "",
    // issuing_entity: "",
    notes: "",
    cost_amount: "",
    cost_payment_date: "",
    cost_payment_method: "transfer",
  });
  const [files, setFiles] = useState([]);
  const isFirstRender = useRef(true);
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };



  // Solo cargar datos iniciales al montar (UNA VEZ)
  useEffect(() => {
    if (user?.empresa_id && isFirstRender.current) {
      console.log("🚀 Cargando datos iniciales...");
      loadInitialData();
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.empresa_id]);

  // Recargar SOLO documentos cuando cambien filtros (CON DEBOUNCE)
  useEffect(() => {
    if (isFirstRender.current) return;
    if (!user?.empresa_id) return;

    const timeoutId = setTimeout(() => {
      console.log("🔍 Aplicando filtros...");
      loadDocuments();
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [filters.status, filters.entityType, filters.documentType, currentPage]);

  // Recargar cuando cambie el search (CON DEBOUNCE LARGO)
  useEffect(() => {
    if (isFirstRender.current) return;
    if (!user?.empresa_id) return;

    const timeoutId = setTimeout(() => {
      console.log("🔎 Buscando:", filters.search);
      loadDocuments();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [filters.search]);
  // ✅ AGREGAR ESTE useEffect NUEVO
  useEffect(() => {
    // Esperar a que user.empresa_id esté disponible
    if (!user?.empresa_id) {
      console.log("⏳ Esperando user.empresa_id...");
      return;
    }

    console.log("🔔 [useEffect Alerts] Iniciando carga de alertas");
    console.log("👤 empresa_id:", user.empresa_id);

    // Cargar inmediatamente
    loadAlerts();

    // Recargar cada 30 segundos
    const interval = setInterval(() => {
      console.log("🔄 [Interval] Recargando alertas automáticamente");
      loadAlerts();
    }, 30000);

    return () => {
      console.log("🧹 Limpiando interval de alertas");
      clearInterval(interval);
    };
  }, [user?.empresa_id]); // ✅ Se ejecuta cuando user.empresa_id cambia
  const loadInitialData = async () => {
    console.log("📦 Cargando datos iniciales...");
    setLoading(true);

    try {
      await Promise.all([
        loadDocuments(),
        loadStats(),
        loadAlerts(),
        loadDocumentTypes(),
        loadVehicles(),
        loadDrivers(),
        loadCompanies(),
      ]);
      console.log("✅ Datos iniciales cargados");
    } catch (error) {
      console.error("❌ Error cargando datos iniciales:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: String(currentPage),
        per_page: String(perPage),
        empresa_id: String(user.empresa_id),
      });

      if (filters.status && filters.status !== "all") {
        params.append("status", filters.status);
      }

      if (filters.entityType && filters.entityType !== "all") {
        params.append("entity_type", filters.entityType);
      }

      if (filters.documentType && filters.documentType !== "all") {
        params.append("document_type_id", filters.documentType);
      }

      console.log("📡 GET /documents?", params.toString());
      console.log("📡 Parámetros enviados:", params.toString());
      console.log("🎯 Filtros actuales:", filters);
      const response = await fetch(`${API_URL}/documents?${params}`, {
        headers: getHeaders(),
      });

      // ✅ MANEJAR ERROR 429 ESPECÍFICAMENTE
      if (response.status === 429) {
        console.error("⚠️ Rate limit alcanzado - esperando...");
        Swal.fire({
          icon: "warning",
          title: "Muchas peticiones",
          text: "Espera unos segundos antes de continuar",
          timer: 2000,
          showConfirmButton: false,
        });
        // Esperar 3 segundos antes de permitir otra petición
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return;
      }

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Documentos:", data.data?.length || 0);
        setDocuments(data.data || []);
        setCurrentPage(data.current_page || 1);
        setTotalPages(data.last_page || 1);
      } else {
        const errorData = await response.json();
        console.error("❌ Error:", errorData);
      }
    } catch (error) {
      console.error("❌ Error de red:", error);
    } finally {
      setLoading(false);
    }
  };
  let loadStatsTimeout = null;
  let loadAlertsTimeout = null;
  const loadStats = async () => {
    // Cancelar petición anterior
    if (loadStatsTimeout) clearTimeout(loadStatsTimeout);

    loadStatsTimeout = setTimeout(async () => {
      try {
        console.log("📊 Cargando stats...");
        const response = await fetch(
          `${API_URL}/documents/stats?empresa_id=${user.empresa_id}`,
          { headers: getHeaders() },
        );

        if (response.status === 429) {
          console.warn("⚠️ Rate limit en stats");
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("❌ Error stats:", error);
      }
    }, 100); // Esperar 100ms antes de ejecutar
  };

  // const loadAlerts = async () => {
  //   // Cancelar petición anterior
  //   if (loadAlertsTimeout) clearTimeout(loadAlertsTimeout);

  //   loadAlertsTimeout = setTimeout(async () => {
  //     try {
  //       console.log("🔔 Cargando alertas...");
  //       const response = await fetch(
  //         `${API_URL}/alerts?empresa_id=${user.empresa_id}&unread_only=true`,
  //         { headers: getHeaders() },
  //       );

  //       if (response.status === 429) {
  //         console.warn("⚠️ Rate limit en alertas");
  //         return;
  //       }

  //       if (response.ok) {
  //         const data = await response.json();
  //         setAlerts(data.data || []);
  //         setUnreadAlertsCount(data.data?.length || 0);
  //       }
  //     } catch (error) {
  //       console.error("❌ Error alertas:", error);
  //     }
  //   }, 100);
  // };
  const loadAlerts = async () => {
    if (loadAlertsTimeout) clearTimeout(loadAlertsTimeout);

    loadAlertsTimeout = setTimeout(async () => {
      try {
        console.log("🔔 Cargando alertas...");
        console.log("👤 user.empresa_id:", user?.empresa_id);

        if (!user?.empresa_id) {
          console.error("❌ No hay empresa_id disponible");
          return;
        }

        const params = new URLSearchParams({
          empresa_id: user.empresa_id,
          unread_only: "true",
          per_page: "50",
        });

        const url = `${API_URL}/alerts?${params.toString()}`;
        console.log("📡 URL de alertas:", url);

        const response = await fetch(url, {
          headers: getHeaders(),
        });

        console.log("📡 Response status:", response.status);

        if (response.status === 429) {
          console.warn("⚠️ Rate limit en alertas");
          return;
        }

        if (response.ok) {
          const data = await response.json();

          console.log("✅ Alertas recibidas:", {
            total: data.total,
            per_page: data.per_page,
            current_page: data.current_page,
            data_length: data.data?.length || 0,
          });

          // ✅ AGREGAR ESTOS LOGS CRÍTICOS
          console.log(
            "🔄 Llamando setAlerts con:",
            data.data?.length || 0,
            "alertas",
          );
          setAlerts(data.data || []);

          console.log(
            "🔄 Llamando setUnreadAlertsCount con:",
            data.data?.length || 0,
          );
          setUnreadAlertsCount(data.data?.length || 0);

          // ✅ VERIFICAR DESPUÉS DE 100ms
          setTimeout(() => {
            console.log(
              "✅ Estado actualizado (debería reflejarse en DevTools)",
            );
          }, 100);

          if (data.data && data.data.length > 0) {
            console.log("📧 Primera alerta:", data.data[0]);
          } else {
            console.warn("⚠️ No se recibieron alertas en data.data");
          }
        } else {
          const errorText = await response.text();
          console.error("❌ Error response:", errorText);
        }
      } catch (error) {
        console.error("❌ Error alertas:", error);
      }
    }, 100);
  };
  const loadDocumentTypes = async () => {
    try {
      console.log("📋 Cargando tipos de documentos...");

      const response = await fetch(
        `${API_URL}/document-types?active_only=true`,
        {
          headers: getHeaders(),
        },
      );

      if (response.ok) {
        const data = await response.json();

        // ✅ LOG DE VERIFICACIÓN
        console.log("✅ Document types cargados:", data.length);

        // ✅ Verificar cuáles tienen campos personalizados
        const conCampos = data.filter(
          (dt) => dt.custom_fields && dt.custom_fields.length > 0,
        );
        console.log(`📝 ${conCampos.length} tipos con campos personalizados:`);
        conCampos.forEach((dt) => {
          console.log(`  - ${dt.name}: ${dt.custom_fields.length} campos`);
        });

        setDocumentTypes(data);
      } else {
        console.error("❌ Error response:", response.status);
        const errorData = await response.json();
        console.error("Error data:", errorData);
      }
    } catch (error) {
      console.error("❌ Error cargando tipos de documentos:", error);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await fetch(`${API_URL}/flota/vehiculos`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (error) {
      console.error("Error cargando vehículos:", error);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await fetch(`${API_URL}/flota/conductores`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      }
    } catch (error) {
      console.error("Error cargando conductores:", error);
    }
  };
  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/empresas`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error("Error cargando empresas:", error);
    }
  };
  const handleReload = async () => {
    if (!canReload) {
      Swal.fire({
        icon: "info",
        title: "Espera un momento",
        text: "Ya hay una recarga en progreso",
        timer: 1500,
        showConfirmButton: false,
      });
      return;
    }

    setCanReload(false);
    await loadInitialData();

    // Bloquear el botón por 5 segundos
    setTimeout(() => {
      setCanReload(true);
    }, 5000);
  };
  const handleStatusFilter = useCallback((status) => {
    setFilters((prev) => ({ ...prev, status }));
    setCurrentPage(1);
  }, []);

  const handleOpenModal = (mode, document = null) => {
    setModalMode(mode);
    setSelectedDocument(document);

    if (mode === "create") {
      // ✅ MODO CREAR: Solo campos universales
      setFormData({
        document_type_id: "",
        documentable_type: "vehicle",
        documentable_id: "",
        issue_date: "",
        expiration_date: "",
        notes: "",
        cost_amount: "",
        cost_payment_date: "",
        cost_payment_method: "transfer",
      });
      setCustomFieldsValues({}); // ✅ Resetear custom fields
      setFiles([]);
    } else if (mode === "view") {
      // ✅ MODO VER: Solo campos universales (custom fields se cargan por separado)
      setFormData({
        document_type_id: document.document_type_id,
        documentable_type: document.documentable_type,
        documentable_id: document.documentable_id,
        issue_date: document.issue_date || "",
        expiration_date: document.expiration_date || "",
        notes: document.notes || "",
        cost_amount: document.costs?.[0]?.amount || "",
        cost_payment_date: document.costs?.[0]?.payment_date || "",
        cost_payment_method: document.costs?.[0]?.payment_method || "transfer",
      });

      // ✅ Cargar custom fields si existen
      setCustomFieldsValues(document.custom_fields || {});
    }

    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDocument(null);
    setFiles([]);
    setCustomFieldsValues({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación básica
    if (!formData.document_type_id || !formData.documentable_id) {
      Swal.fire("Error", "Completa todos los campos obligatorios", "error");
      return;
    }

    // Validar campos personalizados requeridos
    const selectedType = documentTypes.find(
      (dt) => dt.id === formData.document_type_id,
    );

    if (selectedType?.custom_fields) {
      const missingFields = [];

      selectedType.custom_fields.forEach((field) => {
        if (field.required && !customFieldsValues[field.name]) {
          missingFields.push(field.label);
        }
      });

      if (missingFields.length > 0) {
        Swal.fire({
          icon: "error",
          title: "Campos Requeridos",
          html: `Por favor completa los siguientes campos:<br><br><strong>${missingFields.join("<br>")}</strong>`,
        });
        return;
      }
    }

    // ✅ NUEVO: Validar costo si es requerido
    if (selectedType?.requires_cost && !formData.cost_amount) {
      Swal.fire({
        icon: "error",
        title: "Costo Requerido",
        text: "Este tipo de documento requiere registrar el costo",
      });
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      // Campos básicos
      data.append("document_type_id", formData.document_type_id);
      data.append("documentable_type", formData.documentable_type);
      data.append("documentable_id", formData.documentable_id);
      data.append("issue_date", formData.issue_date);
      data.append("expiration_date", formData.expiration_date);
      data.append("notes", formData.notes || ""); //Prevenir null

      // Custom fields
      if (Object.keys(customFieldsValues).length > 0) {
        data.append("custom_fields", JSON.stringify(customFieldsValues));
      }

      // Costo (solo si hay monto)
      if (formData.cost_amount && parseFloat(formData.cost_amount) > 0) {
        data.append("cost[amount]", formData.cost_amount);
        data.append(
          "cost[payment_date]",
          formData.cost_payment_date || formData.issue_date,
        );
        data.append("cost[payment_method]", formData.cost_payment_method);
      }

      // Archivos
      files.forEach((file, index) => {
        data.append(`attachments[${index}]`, file);
      });

      console.log("📤 Enviando documento:", {
        document_type: selectedType.name,
        entity_type: formData.documentable_type,
        has_custom_fields: Object.keys(customFieldsValues).length > 0,
        has_cost: !!formData.cost_amount,
        files_count: files.length,
      });

      const response = await fetch(`${API_URL}/documents`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: data,
      });

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Documento creado:", result.data);

        Swal.fire({
          icon: "success",
          title: "¡Documento Creado!",
          text: "El documento ha sido registrado exitosamente",
          timer: 2000,
          showConfirmButton: false,
        });

        handleCloseModal();

        // Resetear y recargar
        setCurrentPage(1);
        setFilters({
          search: "",
          status: "all",
          entityType: "all",
          documentType: "all",
        });

        setTimeout(() => {
          loadDocuments();
          loadStats();
          loadAlerts();
        }, 500);
      } else {
        const error = await response.json();
        console.error("❌ Error del servidor:", error);
        throw new Error(error.message || "Error al crear documento");
      }
    } catch (error) {
      console.error("❌ Error en handleSubmit:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo crear el documento",
      });
    } finally {
      setLoading(false);
    }
  };
  // const handleMarkAlertAsRead = async (alertId) => {
  //   try {
  //     await fetch(`${API_URL}/alerts/${alertId}/read`, {
  //       method: "POST",
  //       headers: getHeaders(),
  //     });
  //     loadAlerts();
  //   } catch (error) {
  //     console.error("Error marcando alerta como leída:", error);
  //   }
  // };
  const handleMarkAlertAsRead = async (alert) => {
    // ✅ Cambia alertId por alert
    try {
      console.log("👆 Marcando alerta como leída:", alert.id);

      const response = await fetch(`${API_URL}/alerts/${alert.id}/read`, {
        // ✅ alert.id
        method: "POST",
        headers: getHeaders(),
      });

      if (response.ok) {
        console.log("✅ Alerta marcada como leída");
        // Recargar alertas
        loadAlerts();
      } else {
        console.error("❌ Error al marcar alerta:", await response.text());
      }
    } catch (error) {
      console.error("❌ Error marcando alerta como leída:", error);
    }
  };
  const getStatusBadge = (status) => {
    const badges = {
      active: {
        className: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircle,
        label: "Vigente",
      },
      warning: {
        className: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: Clock,
        label: "Por Vencer",
      },
      urgent: {
        className: "bg-orange-100 text-orange-800 border-orange-200",
        icon: AlertTriangle,
        label: "Urgente",
      },
      expired: {
        className: "bg-red-100 text-red-800 border-red-200",
        icon: XCircle,
        label: "Vencido",
      },
    };

    const badge = badges[status] || badges.active;
    const Icon = badge.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badge.className}`}
      >
        <Icon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const getEntityIcon = (type) => {
    const normalizedType = (type || "").toLowerCase();

    if (normalizedType.includes("vehiculo")) {
      return <Truck className="h-4 w-4 text-blue-500" />;
    }

    if (normalizedType.includes("conductor")) {
      return <Users className="h-4 w-4 text-purple-500" />;
    }

    if (normalizedType.includes("empresa")) {
      return <Building2 className="h-4 w-4 text-green-500" />;
    }

    return <FileText className="h-4 w-4 text-gray-500" />;
  };

  const getEntityName = (document) => {
    if (!document?.documentable) {
      console.warn("⚠️ Documento sin documentable:", document);
      return "Sin información";
    }

    try {
      const type = document.documentable_type || "";

      // Soportar namespace completo
      if (type.includes("Vehiculo")) {
        return document.documentable.placa || "Sin placa";
      }

      if (type.includes("Conductor")) {
        const nombre = document.documentable?.nombre || "";
        const apellidos = document.documentable?.apellidos || "";
        return `${nombre} ${apellidos}`.trim() || "Sin nombre";
      }

      if (type.includes("Empresa")) {
        return (
          document.documentable?.nombre_comercial ||
          document.documentable?.razon_social ||
          "Sin nombre"
        );
      }
      console.warn("⚠️ Tipo desconocido:", type);
      return "Desconocido";
    } catch (error) {
      console.error("❌ Error en getEntityName:", error, document);
      return "Error";
    }
  };
  const getAlertLevelBadge = (level) => {
    const badges = {
      info: "bg-blue-100 text-blue-800",
      warning: "bg-yellow-100 text-yellow-800",
      critical: "bg-orange-100 text-orange-800",
      expired: "bg-red-100 text-red-800",
    };
    return badges[level] || badges.info;
  };

  // const filteredDocuments = documents.filter((doc) => {
  //   const searchLower = (filters.search || "").toLowerCase();
  //   const entityName = getEntityName(doc).toLowerCase();
  //   const docNumber = (doc.document_number || "").toLowerCase();
  //   const docType = (doc.document_type?.name || "").toLowerCase();

  //   return (
  //     entityName.includes(searchLower) ||
  //     docNumber.includes(searchLower) ||
  //     docType.includes(searchLower)
  //   );
  // });

  const filteredDocumentTypes = documentTypes.filter((type) => {
    if (formData.documentable_type === "all") return true;
    return type.entity_type === formData.documentable_type;
  });

  // const getEntityOptions = () => {
  //   switch (formData.documentable_type) {
  //     case "vehicle":
  //       return vehicles || [];
  //     case "driver":
  //       return drivers || [];
  //     case "company":
  //       // Si el nombre no viene en el objeto user, podemos usar un fallback
  //       if (user?.empresa_id) {
  //         return [
  //           {
  //             id: user.empresa_id,
  //             // Intentamos obtener el nombre de varias fuentes posibles:
  //             nombre:
  //               user.empresa_nombre ||
  //               user.empresa?.nombre ||
  //               "Nuestra Empresa",
  //           },
  //         ];
  //       }
  //       return [];
  //     default:
  //       return [];
  //   }
  // };
  const getEntityOptions = () => {
    switch (formData.documentable_type) {
      case "vehicle":
        return vehicles || [];
      case "driver":
        return drivers || [];
      case "company":
        // Si el nombre no viene en el objeto user, podemos usar un fallback
        if (user?.empresa_id) {
          return [
            {
              id: user.empresa_id,
              // Intentamos obtener el nombre de varias fuentes posibles:
              nombre:
                user.empresa_nombre ||
                user.empresa?.nombre ||
                "Nuestra Empresa",
            },
          ];
        }
        return [];
      default:
        return [];
    }
  };
  const getEntityLabel = (entity) => {
    if (formData.documentable_type === "vehicle")
      return entity.placa || "Sin placa";
    if (formData.documentable_type === "driver")
      return `${entity.nombre} ${entity.apellidos}`;

    return "";
  };
  // Abrir drawer de detalles
  const handleViewDetail = (doc) => {
    setSelectedDocument(doc);
    setShowDetailDrawer(true);
  };

  // Abrir wizard de renovación
  const handleRenew = (doc) => {
    console.log("🔄 Iniciando renovación:", doc);
    setDocumentToRenew(doc);
    setShowRenewalWarning(true); // ✅ Primero mostrar advertencia
  };
  const handleContinueRenewal = () => {
    console.log("✅ Usuario confirmó renovación");
    setShowRenewalWarning(false);
    setSelectedDocument(documentToRenew);
    setShowRenewalWizard(true);
  };
  const handleCancelRenewal = () => {
    console.log("❌ Usuario canceló renovación");
    setShowRenewalWarning(false);
    setDocumentToRenew(null);
  };

  // Cerrar drawer
  const handleCloseDetail = () => {
    setShowDetailDrawer(false);
    setTimeout(() => setSelectedDocument(null), 300);
  };

  // Cerrar wizard
  const handleCloseRenewal = () => {
    setShowRenewalWizard(false);
  };

  // Submit renovación
  // const handleRenewalSubmit = async (renewalData) => {
  //   try {
  //     const response = await fetch(
  //       `${API_URL}/documents/${selectedDocument.id}/renew`,
  //       {
  //         method: "POST",
  //         headers: getHeaders(),
  //         body: JSON.stringify(renewalData),
  //       },
  //     );

  //     if (response.ok) {
  //       Swal.fire("¡Éxito!", "Documento renovado correctamente", "success");
  //       setShowRenewalWizard(false);
  //       loadInitialData();
  //     }
  //   } catch (error) {
  //     Swal.fire("Error", "No se pudo renovar el documento", "error");
  //   }
  // };
  // Submit renovación
  const handleRenewalSubmit = async (renewalData) => {
    try {
      setLoading(true);

      // ✅ Crear FormData para enviar archivos y datos
      const formData = new FormData();

      // Datos básicos
      formData.append("issue_date", renewalData.issue_date);
      formData.append("expiration_date", renewalData.expiration_date);
      formData.append("document_number", renewalData.document_number || "");
      formData.append("issuing_entity", renewalData.issuing_entity || "");
      formData.append("notes", renewalData.notes || "");

      // ✅ Campos personalizados
      if (
        renewalData.custom_fields &&
        Object.keys(renewalData.custom_fields).length > 0
      ) {
        formData.append(
          "custom_fields",
          JSON.stringify(renewalData.custom_fields),
        );
      }

      // ✅ Costos (si se proporcionó)
      if (renewalData.cost_amount && parseFloat(renewalData.cost_amount) > 0) {
        formData.append("cost[amount]", renewalData.cost_amount);
        formData.append("cost[payment_date]", renewalData.issue_date);
        formData.append(
          "cost[payment_method]",
          renewalData.cost_payment_method || "transfer",
        );
      }

      // ✅ Archivos adjuntos
      if (renewalData.attachments && renewalData.attachments.length > 0) {
        renewalData.attachments.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
      }

      console.log("📤 Enviando renovación:", {
        document_id: selectedDocument.id,
        issue_date: renewalData.issue_date,
        expiration_date: renewalData.expiration_date,
        custom_fields: renewalData.custom_fields,
        has_cost: !!renewalData.cost_amount,
        files_count: renewalData.attachments?.length || 0,
      });

      const response = await fetch(
        `${API_URL}/documents/${selectedDocument.id}/renew`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            // ❌ NO incluir Content-Type cuando se usa FormData
          },
          body: formData,
        },
      );

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Documento renovado:", result);

        Swal.fire({
          icon: "success",
          title: "¡Documento Renovado!",
          text: "El documento ha sido renovado exitosamente",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowRenewalWizard(false);
        setSelectedDocument(null);

        // ✅ Recargar datos
        setTimeout(() => {
          loadDocuments();
          loadStats();
          loadAlerts();
        }, 500);
      } else {
        const error = await response.json();
        throw new Error(error.message || "Error al renovar documento");
      }
    } catch (error) {
      console.error("❌ Error en renovación:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo renovar el documento",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-md border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-2.5 rounded-lg mr-3 shadow-lg">
                <Wrench className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Vigencia de Documentos de Flota
                </h1>
                <p className="text-sm text-gray-600">
                  Control de documentos, vencimientos de vehículos y conductores
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Dropdown de Alertas */}
              <AlertsDropdown
                alerts={alerts}
                unreadCount={unreadAlertsCount}
                onAlertClick={handleMarkAlertAsRead}
              />
              {/* ✅ NUEVO BOTÓN - Gestionar Tipos */}
              <button
                onClick={() => (window.location.href = "/document-types")}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all flex items-center"
              >
                <Settings className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Gestionar Tipos</span>
              </button>

              {/* Botón existente */}
              <button
                onClick={() => handleOpenModal("create")}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Nuevo Documento</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <KPICard
            title="Documentos Activos"
            value={stats.activeDocuments}
            variant="active"
            icon={CheckCircle}
            isActive={filters.status === "active"}
            onClick={() => handleStatusFilter("active")}
          />
          <KPICard
            title="Por Vencer"
            value={stats.warningDocuments}
            variant="warning"
            icon={Clock}
            isActive={filters.status === "warning"}
            onClick={() => handleStatusFilter("warning")}
          />
          <KPICard
            title="Urgentes"
            value={stats.urgentDocuments}
            variant="urgent"
            icon={AlertTriangle}
            isActive={filters.status === "urgent"}
            onClick={() => handleStatusFilter("urgent")}
          />
          <KPICard
            title="Vencidos"
            value={stats.expiredDocuments}
            variant="expired"
            icon={XCircle}
            isActive={filters.status === "expired"}
            onClick={() => handleStatusFilter("expired")}
          />
          {/* <KPICard
            title="Mantenimientos"
            value={stats.pendingMaintenances}
            variant="maintenance"
            icon={Wrench}
          /> */}
        </div>
        {/* Filtros */}
        {/* Filtros */}
        <FleetFilters
          filters={filters}
          onFiltersChange={setFilters}
          documentTypes={documentTypes}
          onReload={handleReload}
          loading={loading}
          canReload={canReload}
        />

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading && documents.length === 0 ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">
                Cargando documentos...
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Referencia / Entidad
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Documento
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Vencimiento
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Alertas
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {documents.length > 0 ? (
                      documents.map((doc, index) => (
                        <tr
                          key={doc.id}
                          className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <div className="p-2 bg-gray-100 rounded-lg mr-3">
                                {getEntityIcon(doc.documentable_type)}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 text-sm">
                                  {getEntityName(doc)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {doc.documentable_type.includes("Vehiculo")
                                    ? "Vehículo"
                                    : doc.documentable_type.includes(
                                      "Conductor",
                                    )
                                      ? "Conductor"
                                      : "Empresa"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-medium text-gray-900 text-sm">
                              {doc.document_number || "-"}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-700 font-medium">
                              {doc.document_type?.name}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-700">
                                {doc.expiration_date
                                  ? new Date(
                                    doc.expiration_date,
                                  ).toLocaleDateString("es-PE")
                                  : "-"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            {getStatusBadge(doc.status)}
                          </td>

                          {/* COLUMNA ALERTAS - VERSIÓN MEJORADA */}
                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center">
                              {doc.alerts && doc.alerts.length > 0 ? (
                                <div className="flex items-center space-x-1">
                                  <span
                                    className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 py-1 rounded-full text-xs font-bold ${doc.alerts.length === 3
                                        ? "bg-red-100 text-red-800 border border-red-300"
                                        : doc.alerts.length === 2
                                          ? "bg-orange-100 text-orange-800 border border-orange-300"
                                          : "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                      }`}
                                  >
                                    {doc.alerts.length}
                                  </span>
                                  {/* Tooltip opcional */}
                                  <span className="text-xs text-gray-500">
                                    {doc.alerts.length === 1 ? "hito" : "hitos"}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </td>

                          {/* COLUMNA ACCIONES - SIEMPRE 4 BOTONES */}
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center space-x-1">
                              {/* Botón Ver */}
                              <button
                                onClick={() => handleViewDetail(doc)} // ✅ Cambiar aquí
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                title="Ver detalles"
                              >
                                <Eye className="h-4 w-4" />
                              </button>

                              {/* Botón Renovar */}
                              <button
                                onClick={() => handleRenew(doc)} // ✅ Cambiar aquí
                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                title="Renovar"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>

                              {/* Botón Costos - SIEMPRE VISIBLE */}
                              <button
                                className={`p-2 rounded-lg transition-colors ${doc.costs && doc.costs.length > 0
                                    ? "text-purple-600 hover:bg-purple-100 cursor-pointer"
                                    : "text-gray-300 cursor-not-allowed opacity-40"
                                  }`}
                                title={
                                  doc.costs && doc.costs.length > 0
                                    ? `Costo: S/. ${doc.costs[0].amount}`
                                    : "Sin costos registrados"
                                }
                                disabled={!doc.costs || doc.costs.length === 0}
                                onClick={(e) => {
                                  if (doc.costs && doc.costs.length > 0) {
                                    // Aquí puedes agregar la lógica para ver los costos
                                    console.log("Ver costos:", doc.costs);
                                  }
                                }}
                              >
                                <DollarSign className="h-4 w-4" />
                              </button>

                              {/* Botón Eliminar */}
                              <button
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-12 text-center">
                          <AlertTriangle className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 font-medium">
                            {filters.search ||
                              filters.status !== "all" ||
                              filters.entityType !== "all"
                              ? "No se encontraron documentos con estos filtros"
                              : "No hay documentos registrados"}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="px-4 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700 font-medium">
                    Página <span className="font-bold">{currentPage}</span> de{" "}
                    <span className="font-bold">{totalPages}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
            {/* Header del Modal */}
            <div className="sticky top-0 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileText className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-xl font-bold text-white">
                    {modalMode === "create"
                      ? "Nuevo Documento"
                      : "Detalles del Documento"}
                  </h3>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Contenido del Modal */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {modalMode === "create" && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Tipo de Entidad *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          documentable_type: "vehicle",
                          documentable_id: "",
                          document_type_id: "", // ✅ Resetear tipo de documento
                        }))
                      }
                      className={`p-4 border-2 rounded-xl transition-all ${formData.documentable_type === "vehicle"
                          ? "border-blue-500 bg-blue-50 shadow-md"
                          : "border-gray-200 hover:border-blue-300"
                        }`}
                    >
                      <Truck className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                      <span className="text-sm font-semibold">Vehículo</span>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          documentable_type: "driver",
                          documentable_id: "",
                          document_type_id: "",
                        }))
                      }
                      className={`p-4 border-2 rounded-xl transition-all ${formData.documentable_type === "driver"
                          ? "border-purple-500 bg-purple-50 shadow-md"
                          : "border-gray-200 hover:border-purple-300"
                        }`}
                    >
                      <Users className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                      <span className="text-sm font-semibold">Conductor</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          documentable_type: "company",
                          documentable_id: "",
                          document_type_id: "",
                        }))
                      }
                      className={`p-4 border-2 rounded-xl transition-all ${formData.documentable_type === "company"
                          ? "border-green-500 bg-green-50 shadow-md"
                          : "border-gray-200 hover:border-green-300"
                        }`}
                    >
                      <Building2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                      <span className="text-sm font-semibold">Empresa</span>
                    </button>
                  </div>
                </div>
              )}

              {modalMode === "create" && (
                <div>
                  <label htmlFor="documentable_id_field" className="block text-sm font-bold text-gray-700 mb-2">
                    Seleccionar{" "}
                    {formData.documentable_type === "vehicle"
                      ? "Vehículo"
                      : formData.documentable_type === "driver"
                        ? "Conductor"
                        : "Empresa"}{" "}
                    *
                  </label>
                  <select
                    id="documentable_id_field"
                    name="documentable_id"
                    value={formData.documentable_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">-- Seleccione --</option>
                    {getEntityOptions().map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.placa
                          ? option.placa
                          : option.nombre ||
                          `${option.nombres || ""} ${option.apellidos || ""}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {modalMode === "create" && (
                <div>
                  <label htmlFor="document_type_id_field" className="block text-sm font-bold text-gray-700 mb-2">
                    Tipo de Documento *
                  </label>
                  <select
                    id="document_type_id_field"
                    name="document_type_id"
                    value={formData.document_type_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">Seleccionar tipo...</option>
                    {filteredDocumentTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {modalMode === "create" &&
                formData.document_type_id &&
                (() => {
                  const selectedType = documentTypes.find(
                    (dt) => dt.id === formData.document_type_id,
                  );

                  if (!selectedType) return null;

                  console.log("📋 Tipo seleccionado:", {
                    name: selectedType.name,
                    requires_cost: selectedType.requires_cost,
                    custom_fields_count:
                      selectedType.custom_fields?.length || 0,
                  });

                  return (
                    <>
                      <div className="border-t-2 border-gray-200 pt-5 mt-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                          📅 Fechas del Documento
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="issue_date_field" className="block text-sm font-bold text-gray-700 mb-2">
                              Fecha de Emisión *
                            </label>
                            <input
                              id="issue_date_field"
                              type="date"
                              name="issue_date"
                              value={formData.issue_date}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            />
                          </div>

                          <div>
                            <label htmlFor="expiration_date_field" className="block text-sm font-bold text-gray-700 mb-2">
                              Fecha de Vencimiento *
                            </label>
                            <input
                              id="expiration_date_field"
                              type="date"
                              name="expiration_date"
                              value={formData.expiration_date}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            />
                          </div>
                        </div>
                      </div>

                      {selectedType.custom_fields &&
                        selectedType.custom_fields.length > 0 && (
                          <CustomFields
                            fields={selectedType.custom_fields}
                            values={customFieldsValues}
                            onChange={setCustomFieldsValues}
                            disabled={loading}
                          />
                        )}

                      {selectedType.requires_cost && (
                        <div className="border-t-2 border-gray-200 pt-5 mt-5">
                          <h3 className="text-md font-bold text-gray-900 mb-4 flex items-center">
                            <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                            Información de Costo *
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="cost_amount_field" className="block text-sm font-medium text-gray-700 mb-2">
                                Monto (S/.) *
                              </label>
                              <input
                                id="cost_amount_field"
                                type="number"
                                step="0.01"
                                name="cost_amount"
                                value={formData.cost_amount}
                                onChange={handleInputChange}
                                placeholder="0.00"
                                required
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                              />
                            </div>

                            <div>
                              <label htmlFor="cost_payment_method_field" className="block text-sm font-medium text-gray-700 mb-2">
                                Método de Pago
                              </label>
                              <select
                                id="cost_payment_method_field"
                                name="cost_payment_method"
                                value={formData.cost_payment_method}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                              >
                                <option value="cash">💵 Efectivo</option>
                                <option value="transfer">
                                  🏦 Transferencia
                                </option>
                                <option value="card">💳 Tarjeta</option>
                                <option value="check">📝 Cheque</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="border-t-2 border-gray-200 pt-5 mt-5">
                        <label htmlFor="attachments_field" className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                          <Paperclip className="h-5 w-5 mr-2 text-gray-600" />
                          Archivos Adjuntos
                        </label>
                        <input
                          id="attachments_field"
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {files.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {files.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg"
                              >
                                <FileText className="h-4 w-4 mr-2 text-blue-500" />
                                <span className="flex-1">{file.name}</span>
                                <span className="text-xs text-gray-500">
                                  {(file.size / 1024).toFixed(1)} KB
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="mt-5">
                        <label htmlFor="notes_field" className="block text-sm font-bold text-gray-700 mb-2">
                          Notas / Observaciones
                        </label>
                        <textarea
                          id="notes_field"
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows={3}
                          placeholder="Información adicional..."
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>
                    </>
                  );
                })()}

              <div className="flex justify-end gap-3 pt-5 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition-colors"
                >
                  Cancelar
                </button>
                {modalMode === "create" && (
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 flex items-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Crear Documento
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Click outside del dropdown de alertas para cerrarlo */}
      {/* {showAlertsDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowAlertsDropdown(false)}
        ></div>
      )} */}
      {/* Drawer de Detalles */}
      <DocumentDetailDrawer
        document={selectedDocument}
        isOpen={showDetailDrawer}
        onClose={handleCloseDetail}
        onRenew={handleRenew}
      />

      {/* Wizard de Renovación */}
      <RenewalWizard
        document={selectedDocument}
        isOpen={showRenewalWizard}
        onClose={handleCloseRenewal}
        onSubmit={handleRenewalSubmit}
      />
      {/* Modal de Advertencia de Renovación */}
      <RenewalWarningModal
        document={documentToRenew}
        isOpen={showRenewalWarning}
        onClose={handleCancelRenewal}
        onContinue={handleContinueRenewal}
      />
    </div>
  );
};

export default FleetManagement;
